import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { cn, notify, slugify } from "../../lib/utils";
import { useFormik } from "formik";
import { initialValues, validationSchemas } from "../../lib/forms-utils";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { TranslatedInput } from "../ui/translated-input";
import { TranslatedTextarea } from "../ui/translated-textarea";
import { listPrintLocationsForForms } from "../../api/admin/print-locations";
import * as Switch from "@radix-ui/react-switch";
import { getTemplateByIdSelectMinimalData } from "../../api/admin/templates";
import TemplateLocationCard from "./template-location-card";
import SelectTemplateDialog from "./select-template-dialog";

const CreateUpdateSetForm = ({
  set,
  onFormSubmission,
  isEditMode,
  btnLoading,
}: any) => {
  // print locations
  const [printLocations, setPrintLocations] = useState<any>([]);

  // save template data when is selected for the set, to show it in cards.
  const [selectedTemplates, setSelectedTemplates] = useState<any>([]);

  const [selectTemplateModalOpen, setSelectTemplateModalOpen] = useState(false);

  const formik = useFormik({
    initialValues: initialValues.setCreateOrUpdate(set),
    validationSchema: validationSchemas.createSet,
    enableReinitialize: true,

    onSubmit: (values) => {
      if (isEditMode) {
        onFormSubmission({
          setId: set._id,
          setData: values,
        });
      } else {
        onFormSubmission({
          setData: values,
        });
      }
    },
  });

  // Handle slug update on internal name blur
  const handleOnNameBlur = (e: any) => {
    const name = e.target.value;
    const slugifiedName = slugify(name);
    formik.setFieldValue("slug", slugifiedName);
  };

  const loadPrintLocationsForForms = async () => {
    const response = await listPrintLocationsForForms();

    if (!response || response.status !== 200) {
      return;
    }

    setPrintLocations(response.data);
  };

  const fetchTemplateById = async (templateId: string) => {
    const response = await getTemplateByIdSelectMinimalData(templateId);

    if (!response || response.status !== 200) {
      return null;
    }

    return response.data;
  };

  const handleAddTemplateToSet = async (templateId: string) => {
    const template = await fetchTemplateById(templateId);

    if (!template) {
      notify({
        type: "error",
        message: "Error loading template",
      });

      return;
    }

    // close the modal
    setSelectTemplateModalOpen(false);

    // append the template to the selectedTemplates
    setSelectedTemplates([...selectedTemplates, template]);

    // get current links length,
    // use it to pre-set order for the new link.
    const currentLinksLength = formik.values.templateLocationLinks.length + 1; // 1-based index

    // update the formik values
    formik.setFieldValue("templateLocationLinks", [
      ...formik.values.templateLocationLinks,
      {
        templateId: templateId,
        printLocation: "",
        order: currentLinksLength, // default order
      },
    ]);
  };

  const handleLinkOrderChange = (
    index: number,
    templateId: string,
    newValue: string
  ) => {
    // use index if there is more than one template in the set with same id.
    // duplicated templates are allowed in the set.

    // find the link by template id.
    const currentLinks = formik.values.templateLocationLinks;

    // use index to get the right link.

    const targetByIndex = currentLinks[index];

    // check it has the template id
    if (targetByIndex.templateId === templateId) {
      // update the print location
      targetByIndex.order = newValue;
    } else {
      // find the first link with the template id.
      const targetByTemplateId = currentLinks.find(
        (link: any) => link.templateId === templateId
      );

      // update the print location
      targetByTemplateId.order = newValue;

      // update the current links
      currentLinks[currentLinks.indexOf(targetByTemplateId)] =
        targetByTemplateId;
    }

    // update the formik values
    formik.setFieldValue("templateLocationLinks", currentLinks);
  };

  const handleLinkLocationChange = (
    index: number,
    templateId: string,
    newLocation: string
  ) => {
    // use index if there is more than one template in the set with same id.
    // duplicated templates are allowed in the set.

    // find the link by template id.
    const currentLinks = formik.values.templateLocationLinks;

    // use index to get the right link.

    const targetByIndex = currentLinks[index];

    // check it has the template id
    if (targetByIndex.templateId === templateId) {
      // update the print location
      targetByIndex.printLocation = newLocation;
    } else {
      // find the first link with the template id.
      const targetByTemplateId = currentLinks.find(
        (link: any) => link.templateId === templateId
      );

      // update the print location
      targetByTemplateId.printLocation = newLocation;

      // update the current links
      currentLinks[currentLinks.indexOf(targetByTemplateId)] =
        targetByTemplateId;
    }

    // update the formik values
    formik.setFieldValue("templateLocationLinks", currentLinks);
  };

  const handleRemoveTemplateLink = (index: number, templateId: string) => {
    const currentLinks = formik.values.templateLocationLinks;

    const targetByIndex = currentLinks[index];

    if (targetByIndex.templateId === templateId) {
      currentLinks.splice(index, 1);

      formik.setFieldValue("templateLocationLinks", currentLinks);
    }
  };

  const loadTemplatesUsedInSetLinks = async (links: any) => {
    const templateIds = links.map((link: any) => link.templateId);

    const fetched: any = [];

    await Promise.all(
      templateIds.map(async (templateId: string) => {
        const template = await fetchTemplateById(templateId);

        if (!template) {
          return;
        }

        fetched.push(template);
      })
    );

    setSelectedTemplates(fetched);
  };

  useEffect(() => {
    loadPrintLocationsForForms();
  }, []);

  useEffect(() => {
    if (set && isEditMode) {
      loadTemplatesUsedInSetLinks(set.templateLocationLinks);
    }
  }, [set]);

  return (
    <div className="flex flex-col space-y-4 h-full w-full">
      <div className={cn("w-full h-full pt-2 flex items-start gap-x-2")}>
        <div className="my-2 w-1/2 flex flex-col items-start">
          <Label className="block text-body mb-2 text-sm px-1">
            Internal Name *
          </Label>
          <Input
            name="internalName"
            type="text"
            id={"internalName"}
            placeholder="Internal Name"
            value={formik.values.internalName}
            onChange={formik.handleChange}
            onBlur={(e) => {
              formik.handleBlur(e);
              handleOnNameBlur(e);
            }}
            error={
              formik.touched.internalName && formik.errors.internalName
                ? (formik.errors.internalName as string)
                : ""
            }
            className="input-ring bg-transparent text-white placeholder"
          />
        </div>

        <div className="my-2 w-1/2 flex flex-col items-start">
          <Label className="block text-body mb-2 text-sm px-1">Slug *</Label>
          <Input
            name="slug"
            type="text"
            id={"slug"}
            placeholder="slug"
            value={formik.values.slug}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.slug && formik.errors.slug
                ? (formik.errors.slug as string)
                : ""
            }
            className="input-ring bg-transparent text-white placeholder"
          />
        </div>
      </div>

      <div className="w-full h-full flex pt-2">
        <TranslatedInput
          name="displayName"
          id="displayName"
          placeholder="Display Name"
          columns={2}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          values={formik.values.displayName}
          errors={formik.errors.displayName}
          touched={formik.touched.displayName}
        />
      </div>

      <div className="w-full h-full pt-2 flex items-end">
        <TranslatedTextarea
          name="description"
          placeholder="Description"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          values={formik.values.description}
          errors={formik.errors.description}
          touched={formik.touched.description}
          columns={2}
          isEnglishMandatory={false}
        />
      </div>

      <div className="w-full h-full pt-2 flex flex-col items-start gap-y-12 py-4">
        <div>
          <Label className="block text-body /mb-2 text-sm px-1">
            Template to Location Map (min 2)
          </Label>

          {formik.errors.templateLocationLinks &&
            formik.touched.templateLocationLinks && (
              <div className="text-xs text-red-600 ps-1">
                You need to select at least 2 templates to form a set. Locations
                are required for each template.
              </div>
            )}
        </div>

        {formik.values.templateLocationLinks.length > 0 &&
          formik.values.templateLocationLinks.map(
            (link: any, index: number) => {
              return (
                <TemplateLocationCard
                  key={link?.templateId + index}
                  link={link}
                  template={selectedTemplates.find(
                    (template: any) => template._id === link.templateId
                  )}
                  printLocations={printLocations}
                  usedLocations={formik.values.templateLocationLinks.map(
                    (link: any) => link.printLocation
                  )}
                  onRemoveTemplate={() => {
                    handleRemoveTemplateLink(index, link.templateId);
                  }}
                  onChangePrintLocation={(
                    templateId: string,
                    newLocation: string
                  ) => {
                    handleLinkLocationChange(index, templateId, newLocation);
                  }}
                  onOrderChange={(templateId: string, newOrder: string) => {
                    handleLinkOrderChange(index, templateId, newOrder);
                  }}
                />
              );
            }
          )}
      </div>

      <div className="w-full h-full pt-2 flex items-end gap-x-2">
        <div className="my-2 w-1/2 flex flex-col items-start">
          <Button
            disabled={btnLoading}
            loading={btnLoading}
            onClick={() => {
              setSelectTemplateModalOpen(true);
            }}
          >
            Add Template & Print Location
          </Button>
        </div>
        <div className="my-2 w-1/2 flex flex-col items-start">
          <Label className="block text-body mb-2 text-sm px-1">Published</Label>
          <Switch.Root
            className="bg-borderColor hover:cursor-pointer ms-1 w-[39px] h-[20px] rounded-full relative focus:shadow-black data-[state=checked]:bg-primary outline-none cursor-default"
            checked={formik.values.isPublished}
            onCheckedChange={(newValue) => {
              formik.setFieldValue("isPublished", newValue);
            }}
          >
            <Switch.Thumb className="block w-[20px] h-[21px] bg-white rounded-full   transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[19px]" />
          </Switch.Root>
        </div>
      </div>

      <div className="py-2 h-full w-full flex items-end justify-end ">
        <Button
          disabled={btnLoading}
          loading={btnLoading}
          onClick={() => {
            formik.handleSubmit();
          }}
        >
          {isEditMode ? "Update Set" : "Create Set"}
        </Button>
      </div>

      <SelectTemplateDialog
        open={selectTemplateModalOpen}
        onClose={() => {
          setSelectTemplateModalOpen(false);
        }}
        onTemplateSelected={(templateId: string) => {
          handleAddTemplateToSet(templateId);
        }}
      />
    </div>
  );
};

export default CreateUpdateSetForm;
